import { SupabaseClient } from "@supabase/supabase-js"
import { getClient } from "../get-client"
import { WAITING } from "../../../utils/gameStates"
import type { Game, StockItem } from "../../../types"

/**
 * Columns selected on the `game` row across the codebase (audit B1).
 *
 * Excludes the legacy `players` UUID[] and `answers` JSONB columns
 * (M8 / 1b stopped reading them, M8 / 2 stopped writing them, M8 / 3
 * will drop them). The `answers` JSONB in particular grew linearly
 * with player count × question count (~16 kB on an 8-player / 20-
 * question game) and rode every 10 s safety-net poll of the `game`
 * row — significant bandwidth saved without any behaviour change.
 */
export const GAME_COLUMNS =
  "id, token, status, current, start, items, version, pairing_code, mode, created_at"

interface PaginatedGamesResult {
  data: Game[]
  nextCursor: number | null
}

export const getAllGames = async (
  from = 0,
  to = 199,
  client?: SupabaseClient | null
): Promise<PaginatedGamesResult> => {
  const cl = client || getClient()
  const { data, error } = await cl
    .from("game")
    .select(GAME_COLUMNS)
    .range(from, to)
    .order("created_at", { ascending: false })
  if (error) {
    throw error
  }
  return {
    data: data as Game[],
    nextCursor: data.length === to - from + 1 ? to + 1 : null,
  }
}

export const resetGame = async (
  uuid: string,
  client?: SupabaseClient | null
): Promise<Game[]> => {
  const cl = client || getClient()
  const { data, error } = await cl
    .from("game")
    .update({ status: WAITING })
    .eq("id", uuid)
    .select(GAME_COLUMNS)
  if (error) {
    throw error
  }
  return data as Game[]
}

export const getGame = async (
  uuid: string,
  client?: SupabaseClient | null
): Promise<Game> => {
  const cl = client || getClient()
  const { data, error } = await cl
    .from("game")
    .select(GAME_COLUMNS)
    .eq("id", uuid)
    .single()
  if (error) {
    throw error
  }
  return data as Game
}

export const getGameStream = async (
  uuid: string,
  client?: SupabaseClient | null
): Promise<Game> => {
  const cl = client || getClient()
  // TODO : Rewrite with subscription.
  const { data, error } = await cl
    .from("game")
    .select(GAME_COLUMNS)
    .eq("id", uuid)
    .single()
  if (error) {
    throw error
  }
  return data as Game
}

export const getStockItems = async (
  uuid: string,
  client?: SupabaseClient | null
): Promise<StockItem[]> => {
  const cl = client || getClient()
  const { data, error } = await cl
    .from("game")
    .select("items")
    .eq("id", uuid)
    .single()
  if (error) {
    throw error
  }
  const items = (data.items as string[]) || []
  const { data: stock, error: stockError } = await cl
    .from("stock")
    .select()
    .in("id", items)
  if (stockError) {
    throw stockError
  }
  // Sort stock items to match the order in game.items
  const itemsOrder = new Map(items.map((id, idx) => [id, idx]))
  const sortedStock = (stock || []) as StockItem[]
  sortedStock.sort(
    (a, b) => (itemsOrder.get(a.id) ?? 0) - (itemsOrder.get(b.id) ?? 0)
  )
  return sortedStock
}

// `updateLegacyAnswers` was deleted in M8 / 2. The normalized `game_answer`
// table is now the single source of truth for player answers; the legacy
// `game.answers` JSONB column will be dropped in M8 / 3.
